import {
  BodyText,
  NextChecklist as Checklist,
  NextAlert,
  NextButton,
  NextSheet,
  SelectBadge,
  SheetContent,
  SheetPortal,
  SheetTrigger,
} from '@cint/wonderment';
import { useEffect, useMemo, useState } from 'react';

interface ChipsProps {
  filters: Record<string, any>;
  filterKey: string;
  onFiltersChange: (id: string, isChecked: boolean) => void;
  selectedPanel?: string;
  filterBy: string;
  filterOptions: {
    label: string;
    id: string;
    created_at?: Date | null;
  }[];
}

const Chips = ({
  filters,
  onFiltersChange,
  filterOptions,
  filterKey,
  selectedPanel,
  filterBy,
}: ChipsProps) => {
  const selectedOptionsWithNames = filters[filterKey]?.map((id) => ({
    id,
    name: filterOptions.find(({ id: optionId }) => id === optionId)?.label,
  }));

  return (
    <div className="flex gap-2 flex-wrap">
      {selectedOptionsWithNames &&
        selectedOptionsWithNames?.map(({ name, id }) => (
          <SelectBadge
            key={id}
            label={filterBy}
            className="capitalize"
            text={name}
            variant="accent"
            onClick={() => onFiltersChange(id, false)}
            isDisabled={id === selectedPanel}
          />
        ))}
    </div>
  );
};

interface ReportFiltersSheetProps {
  selectedPanel?: string;
  filters: Record<string, any>;
  onFiltersChange: (
    updatedFilters: Partial<ReportFiltersSheetProps['filters']>
  ) => void;
  clearedFilterState?: Record<string, any>;
  filterOptions: {
    label: string;
    id: string;
    created_at?: Date | null;
  }[];
  filterKey: string;
  filterBy: string;
  minDate?: Date | null;
}

export const ReportFiltersSheet = ({
  filters,
  filterKey,
  selectedPanel,
  onFiltersChange,
  filterOptions,
  filterBy,
  clearedFilterState = [],
  minDate = null,
}: ReportFiltersSheetProps) => {
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);
  const [effectiveMinDate, setEffectiveMinDate] = useState<Date | null>(
    minDate
  );
  const handleFiltersClear = () => {
    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: clearedFilterState,
    }));
  };

  useEffect(() => {
    if (filters[filterKey]) {
      setTempFilters(filters);
    }
  }, [filterKey, filters]);

  const calculateEffectiveMinDate = (currentFilters: Record<string, any>) => {
    const selectedPanelIds = Object.keys(
      currentFilters[filterKey] || {}
    ).filter((id) => currentFilters[filterKey][id]);

    let earliestCreationDate: Date | null = null;

    if (selectedPanelIds.length > 0) {
      const selectedPanelsWithDates = filterOptions
        .filter(
          (option) => selectedPanelIds.includes(option.id) && option.created_at
        )
        .map((option) => option.created_at)
        .filter((date): date is Date => date !== null && date !== undefined);

      if (selectedPanelsWithDates.length > 0) {
        earliestCreationDate = new Date(
          Math.max(...selectedPanelsWithDates.map((date) => date.getTime()))
        );
      }
    }
    return earliestCreationDate || minDate;
  };

  useEffect(() => {
    const newEffectiveMinDate = calculateEffectiveMinDate(tempFilters);
    setEffectiveMinDate(newEffectiveMinDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempFilters]);

  const handleFiltersApply = () => {
    onFiltersChange(tempFilters);

    if (effectiveMinDate && tempFilters.selectedDates?.from) {
      const fromDate = new Date(tempFilters.selectedDates.from);
      if (fromDate < effectiveMinDate) {
        onFiltersChange({
          selectedDates: {
            from: effectiveMinDate,
            to: tempFilters.selectedDates?.to,
          },
        });
      }
    }

    setSheetOpen(false);
  };

  const handleFiltersToggle = (isOpen) => {
    setSheetOpen(isOpen);
    if (!isOpen) {
      setTempFilters(filters);
    }
  };

  const overLimitSelected =
    tempFilters[filterKey] &&
    Object.values(tempFilters[filterKey]).filter(Boolean).length > 5;
  const noneSelected =
    tempFilters[filterKey] &&
    Object.values(tempFilters[filterKey]).filter(Boolean).length === 0;
  const canApplyFilters = !overLimitSelected && !noneSelected;

  const handleTempFiltersChange = (id: string, value: boolean) => {
    if (id === selectedPanel) return;
    const prevSelected = Array.isArray(tempFilters[filterKey])
      ? tempFilters[filterKey]
      : [];
    const newSelected =
      value === true
        ? [...prevSelected, id]
        : prevSelected.filter((itemId) => itemId !== id);

    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: newSelected,
    }));
  };

  const checklistItems = useMemo(() => {
    return filterOptions.map(({ id, label }) => ({
      id,
      label,
    }));
  }, [filterOptions]);

  const selectedItems = useMemo(() => {
    return filterOptions.reduce(
      (acc, { id }) => {
        return { ...acc, [id]: !!tempFilters?.[filterKey]?.includes(id) };
      },
      {} as Record<string, boolean>
    );
  }, [filterKey, filterOptions, tempFilters]);

  return (
    <NextSheet open={isSheetOpen} onOpenChange={handleFiltersToggle}>
      <SheetTrigger asChild>
        <NextButton text="Compare" />
      </SheetTrigger>
      <SheetPortal>
        <SheetContent
          className="pt-16"
          headerProps={{
            title: `Compare ${filterBy}s`,
            description: `Select up to 5 ${filterBy}s to compare.`,
          }}
          footerProps={{
            primaryButtonProps: {
              text: 'Confirm',
              onClick: handleFiltersApply,
              isDisabled: !canApplyFilters,
            },
            secondaryButtonProps: {
              text: 'Clear all',
              onClick: handleFiltersClear,
            },
            tertiaryAction: (
              <div className="flex gap-1">
                <BodyText fontWeight="bold">
                  {(tempFilters[filterKey] &&
                    Object.values(tempFilters[filterKey]).filter(Boolean)
                      .length) ||
                    0}{' '}
                </BodyText>
                <BodyText>selected</BodyText>
              </div>
            ),
          }}
        >
          <div className="pt-2">
            {!canApplyFilters && overLimitSelected && (
              <NextAlert
                description={`Limit reached. You can select up to 5 ${filterBy}s. To continue, deselect extras until only 5 remain.`}
                iconName="info"
                variant="danger"
                className="text-sm text-left mb-6"
              />
            )}
            {!canApplyFilters && noneSelected && (
              <NextAlert
                description={`You need to select at least 1 ${filterBy}.`}
                iconName="info"
                variant="danger"
                className="text-sm text-left mb-6"
              />
            )}
            {effectiveMinDate && (
              <NextAlert
                description={`Minimum date available: ${effectiveMinDate.toLocaleDateString()}. The panel comparison will reflect data from the latest migration date across selected panels`}
                iconName="calendar"
                variant="warning"
                className="text-sm text-left mb-6"
              />
            )}
            <Chips
              filters={tempFilters}
              onFiltersChange={handleTempFiltersChange}
              filterOptions={filterOptions}
              filterKey={filterKey}
              selectedPanel={selectedPanel}
              filterBy={filterBy}
            />
            <Checklist
              checklistItems={checklistItems || []}
              labelProps={{
                label: '',
              }}
              onCheckedChange={handleTempFiltersChange}
              selectedOptions={selectedItems}
              variant="vertical"
            />
          </div>
        </SheetContent>
      </SheetPortal>
    </NextSheet>
  );
};
